import { Button, Card, Col, Flex, Input, Row, Typography } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BackOfficeTable } from '~/app/components/backOfficeTable/BackOfficeTable';
import { OrganizationDialog } from '~/app/components/organization/OrganizationDialog';
import type { BackOfficePageViewProps } from './BackOfficePage.types';

const { Paragraph, Title } = Typography;

export function BackOfficePageView({
  editingOrganization,
  isDialogOpen,
  isSuperAdmin,
  isTriggeringMetisLogs,
  onAddNew,
  onDelete,
  onDialogOpenChange,
  onEdit,
  onStatusToggle,
  onSearchChange,
  onPageChange,
  onTriggerMetisLogs,
  organizations,
  searchValue,
  total,
  page,
  pageSize,
}: BackOfficePageViewProps) {
  const { t } = useTranslation('organization');

  return (
    <>
      <Flex className="app-page" vertical>
        <Row gutter={[0, 12]}>
          <Col span={24} className="app-backoffice-header">
            <Flex justify="space-between" align="flex-start" gap={16} wrap>
              <div>
                <Title level={2}>{t('page.title')}</Title>
                <Paragraph type="secondary">{t('page.subtitle')}</Paragraph>
              </div>
              {isSuperAdmin ? (
                <Flex
                  vertical
                  align="flex-end"
                  gap={4}
                  style={{ maxWidth: 360 }}
                >
                  <Button
                    loading={isTriggeringMetisLogs}
                    onClick={onTriggerMetisLogs}
                  >
                    {t('page.triggerMetisLogs')}
                  </Button>
                  <Typography.Text
                    type="secondary"
                    style={{ textAlign: 'right' }}
                  >
                    {t('page.triggerMetisLogsInfo')}
                  </Typography.Text>
                </Flex>
              ) : null}
            </Flex>
          </Col>
          <Col span={24}>
            <Card
              title={
                <Flex
                  justify="space-between"
                  align="center"
                  className="app-backoffice-card-header"
                >
                  <Flex vertical>
                    <span className="app-backoffice-card-title">
                      {t('page.organizationsTitle')}
                    </span>
                    <div className="app-backoffice-card-subtitle">
                      {t('page.organizationsSubtitle')}
                    </div>
                  </Flex>
                  <Flex align="center" gap={12}>
                    <Input.Search
                      allowClear
                      placeholder={t(
                        'page.searchPlaceholder',
                        'Search by name',
                      )}
                      value={searchValue}
                      onChange={(event) => onSearchChange(event.target.value)}
                      style={{ width: 280 }}
                    />
                    <Button
                      type="primary"
                      icon={<PlusCircleOutlined />}
                      onClick={onAddNew}
                    >
                      {t('page.create')}
                    </Button>
                  </Flex>
                </Flex>
              }
              styles={{ body: { padding: 0 } }}
            >
              <BackOfficeTable
                organizations={organizations}
                onEdit={onEdit}
                onDelete={onDelete}
                onStatusToggle={onStatusToggle}
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={onPageChange}
              />
            </Card>
          </Col>
        </Row>
      </Flex>

      <OrganizationDialog
        open={isDialogOpen}
        onOpenChange={onDialogOpenChange}
        organization={editingOrganization}
        existingNames={organizations.map((organization) => organization.name)}
        parentOptions={organizations.map((organization) => ({
          value: organization.id,
          label: organization.name,
        }))}
      />
    </>
  );
}
